// The admin-gated `/admin/code-map` provisioning surface. Mirrors AccessPage:
// a `.loom-dashboard`-scoped shell, a server-owned forbidden state (rendered
// off a 403 at the page level, not a client-side role check), and a project
// picker that's structurally identical to AccessPage's AddGrant combobox
// (search fetchProjects(), debounce, list results, click to select).
//
// Once a project is selected, the page owns the fetch/generate round-trip
// through useCodeMapAdmin: it fetches status on selection and re-fetches it
// after a successful generate so the new version/nodeCount/generatedAt show
// immediately.

import { useEffect, useMemo, useState, type JSX } from "react";

import { useT } from "../i18n/index.js";
import { useFormatDate } from "../i18n/format.js";
import { fetchProjects, type Project } from "../projects/useProjects";

import { useCodeMapAdmin } from "./useCodeMapAdmin";
import { useIsAdmin } from "./useIsAdmin";

interface CodeMapAdminPageProps {
  readonly onBack: () => void;
}

export const CodeMapAdminPage = ({ onBack }: CodeMapAdminPageProps): JSX.Element => {
  const t = useT();
  const codeMap = useCodeMapAdmin();
  const isAdmin = useIsAdmin();
  const [project, setProject] = useState<Project | null>(null);
  const [lastResult, setLastResult] = useState<{
    droppedCodePaths: number;
    truncated: boolean;
    scannedFiles: number;
    totalFiles: number;
  } | null>(null);

  const selectProject = (next: Project): void => {
    setProject(next);
    setLastResult(null);
    void codeMap.fetchStatus(next.id);
  };

  const onGenerate = async (): Promise<void> => {
    if (!project) return;
    const result = await codeMap.generate(project.id);
    if (result) {
      setLastResult({
        droppedCodePaths: result.droppedCodePaths,
        truncated: result.truncated,
        scannedFiles: result.scannedFiles,
        totalFiles: result.totalFiles,
      });
      await codeMap.fetchStatus(project.id);
    }
  };

  const shell = (body: JSX.Element): JSX.Element => (
    <div className="loom-dashboard admin-code-map-page">
      <header className="admin-access-head">
        <button className="admin-access-back" onClick={onBack} type="button">
          ← {t("admin.codeMap.back")}
        </button>
        <div>
          <h1 className="admin-access-title">{t("admin.codeMap.title")}</h1>
          <p className="admin-access-subtitle">{t("admin.codeMap.subtitle")}</p>
        </div>
      </header>
      {body}
    </div>
  );

  // `forbidden` only arrives once a request has 403'd — i.e. after a project
  // is picked. The `isAdmin` probe short-circuits that so a non-admin isn't
  // first shown a working picker.
  if (codeMap.forbidden || isAdmin === false) {
    return shell(
      <div className="admin-access-state admin-access-denied">
        <h2>{t("admin.codeMap.denied.title")}</h2>
        <p>{t("admin.codeMap.denied.body")}</p>
      </div>,
    );
  }

  return shell(
    <>
      <ProjectPicker onSelect={selectProject} />
      {project ? (
        <StatusPanel
          projectLabel={project.name_with_namespace}
          state={codeMap}
          lastResult={lastResult}
          onGenerate={() => void onGenerate()}
        />
      ) : null}
    </>,
  );
};

// ---------------------------------------------------------------------------
// Project picker — structurally identical to AccessPage's AddGrant combobox.
// ---------------------------------------------------------------------------

const ProjectPicker = ({
  onSelect,
}: {
  onSelect: (project: Project) => void;
}): JSX.Element => {
  const t = useT();
  // `query` IS the input's text at all times — including right after a pick,
  // when we seed it with the chosen project's name. Deriving the displayed
  // value from `selected` instead made the field look frozen: typing
  // re-filtered the list but never changed the visible text.
  const [query, setQuery] = useState("");
  const [browsing, setBrowsing] = useState(true);
  const [projects, setProjects] = useState<Project[] | null>(null);

  useEffect(() => {
    // A pick seeds `query` with the project name purely for display; don't
    // spend a search round-trip re-finding what the operator just chose.
    if (!browsing) return undefined;
    let cancelled = false;
    const q = query.trim();
    const handle = setTimeout(
      () => {
        fetchProjects(q === "" ? { recent: true } : { search: q })
          .then((next) => {
            if (!cancelled) setProjects(next);
          })
          .catch(() => {
            if (!cancelled) setProjects([]);
          });
      },
      q === "" ? 0 : 250,
    );
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, browsing]);

  const results = useMemo(() => (projects ?? []).slice(0, 8), [projects]);

  return (
    <div className="admin-code-map-picker">
      <label className="admin-access-add-field">
        <span>{t("admin.access.grants.project")}</span>
        <input
          className="admin-access-add-search"
          placeholder={t("admin.access.grants.projectPlaceholder")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setBrowsing(true);
          }}
          onFocus={() => setBrowsing(true)}
        />
      </label>
      {browsing ? (
        <div className="admin-access-add-results">
          {results.length === 0 ? (
            <p className="admin-access-muted">{t("admin.access.grants.noProjects")}</p>
          ) : (
            <ul>
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="admin-access-add-result"
                    onClick={() => {
                      onSelect(p);
                      setQuery(p.name_with_namespace);
                      setBrowsing(false);
                    }}
                  >
                    {p.name_with_namespace}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Status panel — loading/error/no-map/has-map states + the generate action.
// ---------------------------------------------------------------------------

/**
 * Seconds since `active` last flipped true, 0 while inactive. Generation runs
 * ~40s behind a disabled button; a ticking counter is the cheapest way to show
 * the page is alive rather than wedged.
 */
const useElapsedSeconds = (active: boolean): number => {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return undefined;
    }
    const startedAt = Date.now();
    const handle = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(handle);
  }, [active]);

  return seconds;
};

const StatusPanel = ({
  projectLabel,
  state,
  lastResult,
  onGenerate,
}: {
  projectLabel: string;
  state: ReturnType<typeof useCodeMapAdmin>;
  lastResult: {
    droppedCodePaths: number;
    truncated: boolean;
    scannedFiles: number;
    totalFiles: number;
  } | null;
  onGenerate: () => void;
}): JSX.Element => {
  const t = useT();
  const formatDate = useFormatDate({ dateStyle: "medium", timeStyle: "short" });
  const elapsed = useElapsedSeconds(state.generating);

  return (
    <div className="admin-code-map-status">
      <h2 className="admin-code-map-project-label">{projectLabel}</h2>

      {state.error ? (
        <p className="admin-access-state-error">
          {/* Lead with plain language for the one failure an operator hits by
              accident, then the upstream reason — this is an operator surface,
              so the technical detail stays. */}
          {state.errorNoRepo ? `${t("admin.codeMap.noRepo")} ` : null}
          {t(
            state.errorKind === "generate"
              ? "admin.codeMap.generateError"
              : "admin.codeMap.error",
            { error: state.error },
          )}
        </p>
      ) : null}

      {state.loading ? (
        <p className="admin-access-state">{t("admin.codeMap.loading")}</p>
      ) : state.status?.hasMap === true ? (
        <div className="admin-code-map-details">
          <dl className="admin-code-map-facts">
            <dt>{t("admin.codeMap.fact.version")}</dt>
            <dd>{state.status.version}</dd>
            <dt>{t("admin.codeMap.fact.nodeCount")}</dt>
            <dd>{state.status.nodeCount}</dd>
            <dt>{t("admin.codeMap.fact.generatedAt")}</dt>
            <dd>{formatDate(state.status.generatedAtMs)}</dd>
            {state.status.defaultBranchSha ? (
              <>
                <dt>{t("admin.codeMap.fact.sha")}</dt>
                <dd className="admin-code-map-sha">
                  {state.status.defaultBranchSha.slice(0, 8)}
                </dd>
              </>
            ) : null}
          </dl>
          <button
            type="button"
            className="admin-access-add-save"
            disabled={state.generating}
            onClick={onGenerate}
          >
            {state.generating
              ? t("admin.codeMap.generating")
              : t("admin.codeMap.regenerate")}
          </button>
        </div>
      ) : !state.loading ? (
        <div className="admin-code-map-empty">
          <p className="admin-access-muted">{t("admin.codeMap.noMap")}</p>
          <button
            type="button"
            className="admin-access-add-save"
            disabled={state.generating}
            onClick={onGenerate}
          >
            {state.generating ? t("admin.codeMap.generating") : t("admin.codeMap.generate")}
          </button>
        </div>
      ) : null}

      {state.generating ? (
        <p className="admin-code-map-note admin-code-map-progress">
          {t("admin.codeMap.generatingHint")}{" "}
          {elapsed > 0
            ? t("admin.codeMap.generatingElapsed", { seconds: elapsed })
            : null}
        </p>
      ) : null}

      {/* Full coverage is the reassuring case and gets the plain note; a short
          read is the warning, and states the ratio rather than "too big". */}
      {lastResult && !lastResult.truncated ? (
        <p className="admin-code-map-note">
          {t("admin.codeMap.scannedFiles", { count: lastResult.scannedFiles })}
        </p>
      ) : null}
      {lastResult && lastResult.droppedCodePaths > 0 ? (
        <p className="admin-code-map-note">
          {t("admin.codeMap.droppedPaths", { count: lastResult.droppedCodePaths })}
        </p>
      ) : null}
      {lastResult?.truncated ? (
        <p className="admin-code-map-note admin-code-map-warn">
          {t("admin.codeMap.truncated", {
            count: lastResult.scannedFiles,
            total: lastResult.totalFiles,
          })}
        </p>
      ) : null}
    </div>
  );
};
